// Kassa — kassir (agent) ishlaydigan uchta endpoint bilan kontrakt.
// Manba: `AgentMoneyController.fund`, `AgentMoneyController.balance`,
// `GrantController.issue`, va ADR-029 §3 dagi YANGI `subscriber.directory`.
//
// ⚠️ BU QATLAM ATAYLAB `features/subscriber` VA `features/agent` DAN
// MUSTAQIL. Ikkalasida ham o'xshash funksiya bor, lekin ular admin
// ekranlarining ehtiyoji bo'yicha yozilgan: `fundSubscriber` kalitni
// ICHIDA yasaydi (ya'ni qayta urinishda kalit O'ZGARADI — timeout'dan
// keyingi ikkinchi bosishda ikki barobar pul), `issueGrant` esa
// `Idempotency-Key` ni umuman yubormaydi. Kassa uchun kalit tashqaridan
// beriladi va amal davomida o'zgarmaydi (`docs/02-LEDGER.md` §6).
// `07-FRONTEND.md` §6: `features/*` bir-birini import qilmaydi.

import axios from 'axios'
import { z } from 'zod'
import { api } from '@/shared/api/client'
import { operationResultSchema, type OperationResult } from '@/shared/api/operationResult'
import { moneySchema, toMoney, uuidSchema } from '@/shared/api/schemas'
import type { Money } from '@/shared/lib/money'
import type { SubscriberState } from '@/shared/ui/StatusTag'

const SUBSCRIBER_STATES = ['NEW', 'ACTIVE', 'SUSPENDED', 'BLOCKED', 'CLOSED'] as const

// --- Abonent katalogi -------------------------------------------------------

/**
 * `SubscriberDirectoryView.Row` — maydonlar ro'yxati YOPIQ (ADR-029 §3).
 *
 * ⚠️ `balance` KELMASLIGI mumkin va bu NOL EMAS: backend ledger javob
 * bermaganda ataylab `null` qaytaradi (`SubscriberServiceImpl.directory`
 * izohi). `?? 0` yozish butun ro'yxatni qarzdorlar ro'yxatiga
 * aylantirardi va bu ishonarli ko'rinardi.
 *
 * ⚠️ `.nullish()`, `.nullable()` EMAS: backend Jackson'i
 * `non_null` bilan sozlangan, ya'ni bo'sh maydon `null` bo'lib emas,
 * UMUMAN KELMASDAN qoladi.
 */
const directoryRowSchema = z.object({
  id: uuidSchema,
  login: z.string(),
  displayName: z.string().nullish(),
  phone: z.string().nullish(),
  /** Hozir amal qilayotgan paketning KANONIK nomi; `null` = faol paket yo'q */
  planName: z.string().nullish(),
  balance: moneySchema.nullish(),
  state: z.enum(SUBSCRIBER_STATES),
  branchNodeId: uuidSchema.nullish(),
})

const directoryViewSchema = z.object({
  rows: z.array(directoryRowSchema),
  total: z.number(),
})

type DirectoryRowRaw = z.infer<typeof directoryRowSchema>

/**
 * Kassir ko'radigan QISQARTIRILGAN qator (ADR-029 §3).
 *
 * ⚠️ Maydonlar ro'yxati YOPIQ: login, ism, telefon, tarif, balans, holat.
 * Manzil, pasport, IP, to'lov tarixi bu yerda yo'q va qo'shilmaydi —
 * `subscriber.directory` doiraga bo'ysunmaydi, ya'ni kassir HAR QANDAY
 * abonentni ko'radi va evaziga ko'rinish qisqaradi.
 */
export interface DirectoryRow {
  id: string
  login: string
  displayName: string | null
  phone: string | null
  /** Amaldagi paketning kanonik nomi; `null` = faol paket yo'q */
  planName: string | null
  /** `null` = NOMA'LUM (ledger javob bermadi), nol emas */
  balance: Money | null
  state: SubscriberState
  /** Abonent filiali — sotuv ro'yxatidan shu filialda sotilmaydigan paketlar chiqadi */
  branchNodeId: string | null
}

function toDirectoryRow(raw: DirectoryRowRaw): DirectoryRow {
  return {
    id: raw.id,
    login: raw.login,
    displayName: raw.displayName ?? null,
    phone: raw.phone ?? null,
    planName: raw.planName ?? null,
    balance: raw.balance == null ? null : toMoney(raw.balance),
    state: raw.state,
    branchNodeId: raw.branchNodeId ?? null,
  }
}

/**
 * Bir ekranga sig'adigan miqdor.
 *
 * ⚠️ Sahifalash ATAYLAB yo'q. Kassir ro'yxatni varaqlamaydi — u aniq
 * bitta odamni qidiradi va topilgan yigirmata qatordan ko'p bo'lsa,
 * to'g'ri javob "keyingi sahifa" emas, ANIQROQ so'rov (to'liq login yoki
 * telefon raqami).
 */
export const DIRECTORY_LIMIT = 20

/**
 * Abonentni login / telefon / ism bo'yicha qidiradi.
 *
 * ⚠️ Bu endpoint DOIRAGA BO'YSUNMAYDI (`SubscriberDirectoryResource`
 * javadoc'i, ADR-029 §3) — "abonent ixtiyoriy agentga boradi" qoidasining
 * kodi. Evaziga ko'rinish qisqaradi: manzil, hujjat, IP, agent va
 * reseller yo'li javobda UMUMAN yo'q.
 */
export async function searchDirectory(
  query: string,
  signal: AbortSignal,
): Promise<readonly DirectoryRow[]> {
  const response = await api.get<unknown>('/api/subscribers/directory', {
    signal,
    params: { search: query.trim(), page: 0, size: DIRECTORY_LIMIT },
  })
  return directoryViewSchema.parse(response.data).rows.map(toDirectoryRow)
}

// --- Kassirning o'z depoziti ------------------------------------------------

const agentNodeSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  displayName: z.string().nullish(),
  /**
   * ⚠️ `null` BO'LISHI MUMKIN va `z.enum(...)` bu yerda BUTUN ekranni
   * yiqitardi. Tugun turi 2026-08-12 dan dinamik jadval
   * (`platform.org_node_type`), `AgentKind` esa faqat shu servis
   * modellaydigan uchta hisob-kitob turining nomi; noma'lum tur uchun
   * backend ataylab `null` qaytaradi (`AgentKind.fromCode` javadoc'i).
   */
  kind: z.enum(['AGENT', 'RESELLER']).nullish(),
  state: z.enum(['ACTIVE', 'SUSPENDED', 'CLOSED']),
})

export type AgentKind = 'AGENT' | 'RESELLER'

export interface CashierAgent {
  id: string
  code: string
  displayName: string
  /** `null` — «bu tur shu servis modellaydigan uchtadan biri emas» */
  kind: AgentKind | null
}

/**
 * Shu sherik NAQD QABUL QILADIMI — ya'ni «Balansni to'ldirish» tugmasi
 * ko'rsatiladimi.
 *
 * ⚠️ TUR ENDI CHEKLAMAYDI (egasi qarori 2026-08-21: «resellerda agentda
 * bor barcha funksiya bo'ladi»). Ilgari `RESELLER` bu yerda kesilardi,
 * holbuki server unga `agent.fund` huquqini BERADI — ya'ni amal mumkin
 * edi, lekin tugma ko'rinmasdi. Bu jimgina yolg'on edi.
 *
 * Qolgan yagona shart — kassirning tuguni umuman bormi. Haqiqiy qarorni
 * server qabul qiladi (ADR-024) va rad javobi ekranda sabab bilan chiqadi.
 */
export function takesCash(agent: CashierAgent | null): boolean {
  return agent !== null
}

/**
 * Kassirning O'Z tuguni.
 *
 * ⚠️ `GET /api/agents/roots` agent uchun FAQAT o'zini qaytaradi (doira
 * `OWN`), ya'ni ro'yxatda bitta yozuv bo'ladi. Adminda esa bir necha
 * ildiz bo'lishi mumkin — o'shanda kassa ekrani "kimning depoziti"
 * degan savolga javob bera olmaydi va tanlov chaqiruvchiga qoladi.
 *
 * ⚠️ Filtr `state === 'ACTIVE'` — `!== 'CLOSED'` EMAS. Backend
 * (`DepositServiceImpl.fund`) `SUSPENDED` agentni ham rad etadi
 * (`AgentState.canTransact()` faqat `ACTIVE` da rost), ya'ni yopilganini
 * chiqarib tashlash tekshiruvni yarim qoldirardi.
 */
export async function fetchCashierAgents(): Promise<CashierAgent[]> {
  const response = await api.get<unknown>('/api/agents/roots')
  return z
    .array(agentNodeSchema)
    .parse(response.data)
    .filter((node) => node.state === 'ACTIVE')
    .map((node) => ({
      id: node.id,
      code: node.code,
      displayName: node.displayName ?? node.code,
      // ⚠️ `?? 'AGENT'` QO'YMANG: taxminiy tur "naqd oladi" degan
      // YOLG'ONNI ekranga chiqarardi. Noma'lum — noma'lum bo'lib qolsin.
      kind: node.kind ?? null,
    }))
}

const balanceSchema = z.object({
  balance: moneySchema,
  available: moneySchema,
})

export interface CashierBalance {
  /** Depozitda haqiqatan turgan summa */
  balance: Money
  /** Balans + qarz limiti — hozir sarflash mumkin bo'lgani */
  available: Money
}

export async function fetchCashierBalance(agentId: string): Promise<CashierBalance> {
  const response = await api.get<unknown>(`/api/agents/${agentId}/balance`)
  const parsed = balanceSchema.parse(response.data)
  return { balance: toMoney(parsed.balance), available: toMoney(parsed.available) }
}

const perTxnSchema = z.object({
  limits: z.record(z.string(), z.number()).nullish(),
})

/**
 * BITTA to'lovning shifti — `fund-limits` javobidagi `PER_TXN`, MINOR
 * birlikda. `null` — chegara qo'yilmagan.
 *
 * ⚠️ SUKUT QIYMAT (100 000 AFN) BU YERGA YOZILMAYDI. Tenant chegarani
 * o'zgartirgan kuni koddagi son ekranda ESKI shiftni ko'rsatib turardi
 * va u ishonarli ko'rinardi — kassir esa serverdan kutilmagan rad javobi
 * olardi.
 *
 * ⚠️ `features/profile` dagi `fetchFundLimits` IMPORT QILINMADI:
 * `07-FRONTEND.md` §6 — `features/*` bir-birini import qilmaydi. Bu
 * yerdagi savol ham torroq: kassaga faqat SHIFT kerak, kunlik/oylik
 * hisoblagichlar esa umuman ishlatilmaydi.
 */
export async function fetchPerTxnLimit(agentId: string): Promise<number | null> {
  const response = await api.get<unknown>(`/api/agents/${agentId}/fund-limits`)
  return perTxnSchema.parse(response.data).limits?.PER_TXN ?? null
}

// --- Pul amallari -----------------------------------------------------------

/**
 * Abonent balansini kassir depozitidan to'ldiradi.
 *
 * ⚠️ MABLAG' YETMASA JAVOB `200` VA ICHIDA `FAILED` — bu texnik xato EMAS,
 * kutilgan biznes holati (`AgentMoneyController.fund` javadoc'i,
 * `CLAUDE.md` §8). Shuning uchun funksiya XATO TASHLAMAYDI va chaqiruvchi
 * ikki holatni ajratishi shart.
 *
 * ⚠️ `idempotencyKey` TASHQARIDAN keladi va bu shu faylning sababi.
 * Kalit SO'ROV uchun emas, AMAL uchun yasaladi: tarmoq uzilib qayta
 * urinilsa yoki tugma qayta bosilsa u O'ZGARMASLIGI shart — aks holda
 * server ikkinchi so'rovni yangi to'lov deb qabul qiladi va abonentga
 * ikki barobar pul tushadi (`shared/lib/idempotency.ts`).
 */
export async function fundSubscriber(
  agentId: string,
  subscriberId: string,
  amount: Money,
  idempotencyKey: string,
): Promise<OperationResult> {
  const response = await api.post<unknown>(`/api/agents/${agentId}/fund`, amount, {
    params: { subscriberId },
    headers: { 'Idempotency-Key': idempotencyKey },
  })
  return operationResultSchema.parse(response.data)
}

const grantSchema = z.object({
  id: uuidSchema,
  subscriberId: uuidSchema,
  planId: uuidSchema.nullish(),
  expiresAt: z.string().nullish(),
  /**
   * ⚠️ «Bu javob TAKROR» belgisi — `GrantIssue.replay`.
   *
   * Backendda u `@JsonUnwrapped` bilan yozilgan, ya'ni grant maydonlari
   * bilan BIR darajada turadi (`data.replay`, `data.grant.replay` EMAS).
   *
   * ⚠️ `.nullish()` va `false` DEB HISOBLANMAYDI: maydonni bermaydigan
   * backend versiyasida `parse` yiqilsa, paket berish umuman ishlamay
   * qolardi. Noma'lum — «takror emas» degan taxminga aylanmasin, shuning
   * uchun chaqiruvchiga `boolean` emas, `replay === true` beriladi.
   */
  replay: z.boolean().nullish(),
})

export type IssuedGrant = z.infer<typeof grantSchema>

/**
 * Paket berish natijasi.
 *
 * ⚠️ `INSUFFICIENT_FUNDS` — `FAILED` DAN BOSHQA HOLAT va boshqa xabar.
 * Balans to'ldirishda pul KASSIRNING depozitidan yechiladi va yetmaslik
 * `200 + FAILED` bilan keladi; paket berishda esa pul ABONENTNING
 * hisobidan yechiladi va yetmaslik `422` bilan keladi
 * (`GrantController.issue` javadoc'i). Ikkalasini bitta "xato" ga
 * yig'ish kassirga NOTO'G'RI ish aytardi: birinchisida o'z depozitini
 * to'ldirishi kerak, ikkinchisida abonentning balansini.
 */
export type GrantOutcome =
  | {
      kind: 'ISSUED'
      grant: IssuedGrant
      /**
       * ⚠️ SERVER «bu men allaqachon bergan paket» DEDI.
       *
       * Bu xato emas va tashvish ham emas — aynan idempotentlik ishlagani:
       * pul IKKINCHI marta yechilmadi. Lekin kassirga «berildi» deb aytish
       * ham noto'g'ri bo'lardi: u ekranni abonentga ko'rsatadi va ikkinchi
       * paket kutilib qolardi. Shuning uchun matn boshqacha.
       */
      replay: boolean
    }
  | { kind: 'INSUFFICIENT_FUNDS'; serverText: string | null }

function insufficientFunds(error: unknown): string | null | false {
  if (!axios.isAxiosError(error) || error.response?.status !== 422) return false
  const body: unknown = error.response.data
  if (body && typeof body === 'object' && 'error' in body) {
    const envelope = (body as { error?: { text?: unknown } | null }).error
    if (envelope && typeof envelope.text === 'string') return envelope.text
  }
  return null
}

/**
 * Abonentga paket beradi — pul ABONENTNING o'z balansidan yechiladi.
 *
 * <h2>⚠️ `Idempotency-Key` ENDI MAJBURIY</h2>
 * `GrantController.issue` sarlavhani o'qiydi va uni SO'RAYDI:
 * kalitsiz — `400`, o'sha kalit bilan takror — `200` va tanada
 * `replay: true`, band kalit bilan BOSHQA amal (boshqa abonent yoki
 * boshqa paket) — `409` va `error.tag=IDEMPOTENCY_CONFLICT`.
 *
 * ⚠️ Oxirgi holat kalitni AMALGA bog'lashni majburiy qiladi: bitta
 * kalitni ikki xil paket uchun ishlatib bo'lmaydi. Chaqiruvchi kalitni
 * `(abonent, paket)` imzosidan hosil qiladi (`useCombinedSale`), ya'ni
 * qayta urinish o'sha kalit bilan ketadi (takror), paket almashtirilsa
 * esa yangi kalit yasaladi (ziddiyat yo'q).
 *
 * ⚠️ `409` bu yerda USHLANMAYDI va yuqoriga tashlanadi. U kassir tuzata
 * oladigan holat emas — panel kalitni noto'g'ri qayta ishlatgan degani,
 * ya'ni dasturiy nuqson; `translateError` uni `tag` bo'yicha tarjima
 * qiladi va sotuvni qaytadan boshlashni taklif etadi.
 */
export async function issueGrant(
  subscriberId: string,
  planId: string,
  idempotencyKey: string,
): Promise<GrantOutcome> {
  try {
    const response = await api.post<unknown>(
      '/api/grants',
      { subscriberId, planId, source: 'PLAN', fundedBy: 'SUBSCRIBER', charge: true },
      { headers: { 'Idempotency-Key': idempotencyKey } },
    )
    const grant = grantSchema.parse(response.data)
    return { kind: 'ISSUED', grant, replay: grant.replay === true }
  } catch (error) {
    const text = insufficientFunds(error)
    if (text === false) throw error
    return { kind: 'INSUFFICIENT_FUNDS', serverText: text }
  }
}
